import React, { useMemo } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiYoutube, FiExternalLink, FiAlertCircle } = FiIcons;

const DEFAULT_CHANNEL_ID = 'UC_oufPfm1xq6BCxxu1jZRvg';
const DEFAULT_CHANNEL_URL = `https://www.youtube.com/channel/${DEFAULT_CHANNEL_ID}`;
const DEFAULT_CHANNEL_KEY = 'AOXaniawfc';

const getChannelKey = (channelUrl) => {
  if (!channelUrl) return DEFAULT_CHANNEL_KEY;

  try {
    const url = new URL(channelUrl);
    const handleMatch = url.pathname.match(/^\/@([^/?#]+)/i);
    if (handleMatch?.[1]) return handleMatch[1];

    const userMatch = url.pathname.match(/^\/user\/([^/?#]+)/i);
    if (userMatch?.[1]) return userMatch[1];

    const channelMatch = url.pathname.match(/^\/channel\/(UC[^/?#]+)/i);
    if (channelMatch?.[1]) {
      return channelMatch[1] === DEFAULT_CHANNEL_ID ? DEFAULT_CHANNEL_KEY : channelMatch[1];
    }
  } catch (error) {
    // Use the known AOX handle as display fallback.
  }

  return DEFAULT_CHANNEL_KEY;
};

const getPlaylistId = (channelUrl, explicitPlaylistId) => {
  if (explicitPlaylistId) return explicitPlaylistId;
  if (!channelUrl) return `UU${DEFAULT_CHANNEL_ID.slice(2)}`;

  try {
    const url = new URL(channelUrl);
    const list = url.searchParams.get('list');
    if (list) return list;

    const channelMatch = url.pathname.match(/^\/channel\/(UC[^/?#]+)/i);
    if (channelMatch?.[1]) {
      return `UU${channelMatch[1].slice(2)}`;
    }

    const handleMatch = url.pathname.match(/^\/@([^/?#]+)/i);
    if (handleMatch?.[1]?.toLowerCase() === DEFAULT_CHANNEL_KEY.toLowerCase()) {
      return `UU${DEFAULT_CHANNEL_ID.slice(2)}`;
    }
  } catch (error) {
    return '';
  }

  return '';
};

const YouTubeChannelEmbed = ({
  channelUrl = DEFAULT_CHANNEL_URL,
  playlistId = '',
  title = 'Τελευταία Videos από το YouTube',
  description = 'Δείτε τα τελευταία videos του ΑΟ Χανιά WFC.',
  compact = false,
}) => {
  const safeChannelUrl = channelUrl || DEFAULT_CHANNEL_URL;
  const channelKey = useMemo(() => getChannelKey(safeChannelUrl), [safeChannelUrl]);
  const resolvedPlaylistId = useMemo(
    () => getPlaylistId(safeChannelUrl, playlistId),
    [safeChannelUrl, playlistId]
  );

  const embedUrl = resolvedPlaylistId
    ? `https://www.youtube-nocookie.com/embed/videoseries?list=${encodeURIComponent(resolvedPlaylistId)}&rel=0&hl=el`
    : '';

  return (
    <div className={`bg-white rounded-xl ${compact ? '' : 'shadow-lg'} overflow-hidden`}>
      <div className={compact ? 'p-4' : 'p-6'}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiYoutube} className="w-7 h-7 text-red-600" />
            </div>
            <div>
              <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>{title}</h3>
              <p className="text-sm text-gray-600">@{channelKey}</p>
            </div>
          </div>

          <a
            href={safeChannelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
          >
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 mr-1.5" />
            Κανάλι YouTube
          </a>
        </div>

        {description && <p className="text-gray-600 mb-4">{description}</p>}

        {embedUrl ? (
          <div className="relative w-full overflow-hidden rounded-lg bg-black" style={{ paddingTop: '56.25%' }}>
            <iframe
              title={`${title} - AO Xania WFC`}
              src={embedUrl}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        ) : (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-6 text-center">
            <SafeIcon icon={FiAlertCircle} className="w-7 h-7 text-amber-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-900 mb-1">Δεν είναι διαθέσιμο το YouTube playlist.</p>
            <p className="text-sm text-gray-600 mb-4">
              Ανοίξτε το επίσημο κανάλι του ΑΟ Χανιά WFC για να δείτε τα τελευταία videos.
            </p>
            <a
              href={safeChannelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              <SafeIcon icon={FiYoutube} className="w-4 h-4 mr-2" />
              Άνοιγμα καναλιού YouTube
            </a>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            {embedUrl ? 'Τα τελευταία uploads προβάλλονται αυτόματα από το επίσημο κανάλι.' : 'Ο σύνδεσμος οδηγεί στο επίσημο κανάλι του ΑΟ Χανιά WFC.'}
          </p>
          <a
            href={`${safeChannelUrl}?sub_confirmation=1`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <SafeIcon icon={FiYoutube} className="w-4 h-4 mr-2" />
            Εγγραφή στο YouTube
          </a>
        </div>
      </div>
    </div>
  );
};

export default YouTubeChannelEmbed;
